import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
function _callSuper(t, o, e) { return o = _getPrototypeOf(o), _possibleConstructorReturn(t, _isNativeReflectConstruct() ? Reflect.construct(o, e || [], _getPrototypeOf(t).constructor) : o.apply(t, e)); }
function _isNativeReflectConstruct() { try { var t = !Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); } catch (t) {} return (_isNativeReflectConstruct = function _isNativeReflectConstruct() { return !!t; })(); }
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import flow from 'lodash/fp/flow';
import filter from 'lodash/fp/filter';
import identity from 'lodash/fp/identity';
import sortBy from 'lodash/fp/sortBy';
import indexOf from 'lodash/fp/indexOf';
import { findTabbable } from '@instructure/ui-dom-utils';

/**
 * Utility function to abstract away vendor-specific equivalents to HTMLel.matches
 */
export function matches(el, selector, textContent) {
  var match = (el.matches || el.matchesSelector || el.mozMatchesSelector || el.msMatchesSelector || el.oMatchesSelector || el.webkitMatchesSelector).call(el, selector);
  return textContent ? match && el.innerText.includes(textContent) : match;
}

/**
 * Returns a list of focusable HTML elements inside of parentEl that match selector
 */
function _getFocusables(parentEl, selector, textContent) {
  return flow(findTabbable, !selector ? identity : filter(function (el) {
    return matches(el, selector, textContent);
  }), sortBy(function (el) {
    return el.tabIndex || 0;
  }))(ReactDOM.findDOMNode(parentEl));
}

/**
 * Safeguard against somehow focusing on already removed HTML elements
 */
export { _getFocusables as getFocusables };
export function tryFocus(el) {
  try {
    el.focus();
  } catch (_unused) {
    console.warn('You tried to focus ' + el + ' but it is not in the DOM anymore');
  }
}

/**
 * Helper Component that is used to handle focus within its children
 * It is used through a ref and directly calling the internal functions
 */
var FocusGroup = /*#__PURE__*/function (_Component) {
  function FocusGroup() {
    var _this;
    _classCallCheck(this, FocusGroup);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _callSuper(this, FocusGroup, [].concat(args));
    /**
     * a cache of focusables as an object with selectors as keys
     * note: this is cleaned on each render
     */
    _defineProperty(_this, "focusableCache", {});
    return _this;
  }
  _inherits(FocusGroup, _Component);
  return _createClass(FocusGroup, [{
    key: "getFocusables",
    value:
    /**
     * returns a list of focusable HTML elements
     * that are inside of FocusGroup & match selector
     */
    function getFocusables(selector, textContent) {
      if (!this.focusableCache[selector]) {
        this.focusableCache[selector] = _getFocusables(this, selector, textContent);
      }
      return this.focusableCache[selector];
    }

    /**
     * focuses the first matching HTML element
     */
  }, {
    key: "focusFirst",
    value: function focusFirst(selector, textContent) {
      var focusables = this.getFocusables(selector, textContent);
      if (focusables.length > 0) {
        tryFocus(focusables[0]);
      }
    }

    /**
     * focuses the last matching HTML element
     */
  }, {
    key: "focusLast",
    value: function focusLast(selector, textContent) {
      var lastFocusable = this.getFocusables(selector, textContent).slice(-1);
      if (lastFocusable.length === 1) {
        tryFocus(lastFocusable[0]);
      }
    }

    /**
     * returns if there's a matching HTML element after the currently focused one
     */
  }, {
    key: "nextExists",
    value: function nextExists(selector, textContent) {
      var focusables = this.getFocusables(selector, textContent);
      var index = indexOf(document.activeElement, focusables);
      return focusables.length > 0 && index !== focusables.length - 1;
    }

    /**
     * focuses the matching HTML element after the currently focused one
     */
  }, {
    key: "focusNext",
    value: function focusNext(selector, textContent) {
      var focusables = this.getFocusables(selector, textContent);
      if (focusables.length === 0) {
        return;
      }
      var index = indexOf(document.activeElement, focusables);
      if (index === -1) {
        tryFocus(focusables[0]);
      } else if (index === focusables.length - 1) {
        if (this.props.wrap) {
          tryFocus(focusables[0]);
        }
      } else {
        tryFocus(focusables[index + 1]);
      }
    }

    /**
     * returns if there's a matching HTML element before the currently focused one
     */
  }, {
    key: "previousExists",
    value: function previousExists(selector, textContent) {
      var focusables = this.getFocusables(selector, textContent);
      var index = indexOf(document.activeElement, focusables);
      return focusables.length > 0 && index !== 0;
    }

    /**
     * focuses the matching HTML element before the currently focused one
     */
  }, {
    key: "focusPrevious",
    value: function focusPrevious(selector, textContent) {
      var focusables = this.getFocusables(selector, textContent);
      if (focusables.length === 0) {
        return;
      }
      var index = indexOf(document.activeElement, focusables);
      if (index === -1) {
        tryFocus(focusables[focusables.length - 1]);
      } else if (index === 0) {
        if (this.props.wrap) {
          tryFocus(focusables[focusables.length - 1]);
        }
      } else {
        tryFocus(focusables[index - 1]);
      }
    }

    /**
     * render as the given Component
     */
  }, {
    key: "render",
    value: function render() {
      var Component = this.props.asComponent;

      // Clean cache
      this.focusableCache = {};
      return /*#__PURE__*/React.createElement(Component, this.props.asProps, this.props.children);
    }
  }]);
}(Component);
_defineProperty(FocusGroup, "propTypes", {
  /**
   * determines if all previous & next functions should wrap around
   */
  wrap: PropTypes.bool,
  /**
   * the Component to be rendered in place of FocusGroup
   */
  asComponent: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
  /**
   * the props being passed to asComponent
   */
  asProps: PropTypes.object,
  /**
   * the children of asComponent
   */
  children: PropTypes.node
});
_defineProperty(FocusGroup, "defaultProps", {
  wrap: false,
  asComponent: 'div',
  asProps: {},
  children: void 0
});
export { FocusGroup as default };